import { Combobox } from '../../components/ui/Combobox';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { normalizeTagName } from '@qkindex/contracts';
import type { ManagementSharingResultMap } from '@qkindex/contracts';
import { manageSharing, ShareTag } from './sharingService';
import { TagSelectionChip } from './TagSelectionChip';
import { SelectionField } from '../../components/ui/SelectionField';
import { TagPill } from './TagPill';

export interface ItemTagPickerProps {
  collectionId?: string;
  collectionName?: string;
  showCollectionName?: boolean;
  value: string[];
  canManage: boolean;
  disabled?: boolean;
  onChange(value: string[]): void;
  onManageTags?(collectionId: string): void;
  onPendingChange?(pending: boolean): void;
}

/** Both item modals use the collection's existing tag creation/list workflow. */
export function ItemTagPicker({
  collectionId,
  collectionName,
  showCollectionName,
  value,
  canManage,
  disabled = false,
  onChange,
  onManageTags,
  onPendingChange,
}: ItemTagPickerProps) {
  const [tags, setTags] = useState<ShareTag[]>([]);
  const [shares, setShares] = useState<
    ManagementSharingResultMap['listTags']['sharedTags']
  >([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    onPendingChange?.(creating);
    return () => onPendingChange?.(false);
  }, [creating, onPendingChange]);
  const load = async () => {
    if (!collectionId) return;
    setLoading(true);
    try {
      const result = await manageSharing(collectionId, 'listTags');
      if (alive.current) {
        setTags(result.tags || []);
        setShares(result.sharedTags);
        setError('');
      }
    } catch {
      if (alive.current) setError('Tags could not be loaded.');
    } finally {
      if (alive.current) setLoading(false);
    }
  };
  useEffect(() => {
    void load(); /* keyed by collection in the item workflow */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);
  const label = (tag: ShareTag) =>
    `${showCollectionName && collectionName ? `${collectionName}/` : ''}${tag.name}`;
  const options = tags.filter((tag) => tag.state === 'active');
  const selected: ShareTag[] = value.map(
    (tagId) =>
      options.find((tag) => tag.tagId === tagId) || {
        tagId,
        name: 'Unavailable tag',
        state: 'archived',
        revision: 0,
      },
  );
  const createTag = async (name: string) => {
    if (!collectionId) return;
    setCreating(true);
    setError('');
    try {
      const result = await manageSharing(collectionId, 'saveTag', {
        name: name.trim(),
      });
      if (!result.tagId) throw new Error('Tag creation failed.');
      if (!alive.current) return;
      const tag: ShareTag = {
        tagId: result.tagId,
        name: name.trim(),
        state: 'active',
        revision: 0,
      };
      setTags((previous) => [...previous, tag]);
      onChange([...value, tag.tagId]);
    } catch (failure) {
      if (alive.current) {
        await load();
        setError(
          failure instanceof Error
            ? failure.message
            : 'Tag could not be created.',
        );
      }
    } finally {
      if (alive.current) setCreating(false);
    }
  };
  return (
    <SelectionField
      label="Tags"
      feedback={
        error ? (
          <Alert
            severity="error"
            action={
              <Button
                onClick={() => {
                  void load();
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : undefined
      }
    >
      {selected.map((tag) => (
        <TagSelectionChip
          key={tag.tagId}
          tagId={tag.tagId}
          label={label(tag)}
          shared={
            canManage && shares.some((share) => share.tagId === tag.tagId)
          }
          onRemove={
            canManage && !disabled && !creating
              ? () => onChange(value.filter((id) => id !== tag.tagId))
              : undefined
          }
        />
      ))}
      {!collectionId ? (
        <Typography variant="body2" color="text.secondary">
          Choose a collection using Location first.
        </Typography>
      ) : canManage ? (
        <Combobox<ShareTag>
          multiple
          addLabel="Add tag"
          label="Tags"
          inputAriaLabel="Add tag"
          placeholder="Type to search tags…"
          sx={{ maxWidth: '100%' }}
          options={options}
          searchAction={
            inputValue.trim() &&
            inputValue.trim().length <= 200 &&
            value.length < 20 &&
            !tags.some(
              (tag) =>
                normalizeTagName(tag.name) ===
                normalizeTagName(inputValue.trim()),
            )
              ? {
                  label: `Create “${inputValue.trim()}”`,
                  onClick: () => {
                    void createTag(inputValue.trim());
                  },
                }
              : undefined
          }
          footerAction={
            onManageTags
              ? {
                  label: 'Manage tags',
                  onClick: () => onManageTags(collectionId),
                }
              : undefined
          }
          value={selected}
          loading={loading}
          disabled={disabled || Boolean(error)}
          busy={creating}
          onSearchChange={setInputValue}
          getOptionKey={(option) => option.tagId}
          getOptionLabel={label}
          getOptionDisabled={(option) =>
            !value.includes(option.tagId) &&
            (option.state !== 'active' || value.length >= 20)
          }
          renderOptionContent={(option) => (
            <TagPill
              variant="body1"
              tag={{ tagId: option.tagId, name: label(option) }}
            />
          )}
          onChange={(next) => onChange(next.map((option) => option.tagId))}
        />
      ) : (
        !selected.length && (
          <Typography variant="body2" color="text.secondary">
            No tags
          </Typography>
        )
      )}
    </SelectionField>
  );
}
